import type { ReactElement, ReactNode } from 'react';
import { ButtonLink, Icon, StatusPill, cx, formatCount } from '../ui';
import s from './Home.module.css';

/** One item of an owner's setup checklist (FR3, FR4), worked out from real settings. */
export interface SetupStepState {
  id: string;
  done: boolean;
  /** Shown but never holds the checklist open. */
  optional?: boolean;
  title: string;
  /** The short status beside the title ("5 of 6", "Please confirm"); none when done. */
  status?: string | null;
  /** The value once it's set, on the folded line ("$77.95 per share from Apr 15, 2026"). */
  summary?: string | null;
  body: string;
  /** "Last changed by … on …". */
  changed?: ReactNode;
  /** Where it's set. */
  action?: { label: string; href: string } | null;
  /** An extra control beside the action (a Confirm button). */
  extra?: ReactNode;
  /** More detail under the body (the templates grid). */
  detail?: ReactNode;
}

/**
 * An owner's setup checklist: open items first, numbered, each with where it's set; done items
 * fold into one line each (DESIGN-SPEC §5 "Owners' setup"). The card around it decides when the
 * whole list goes.
 */
export function SetupSteps({
  label,
  steps,
  doneCount,
  requiredCount,
  footer,
}: {
  label: string;
  steps: readonly SetupStepState[];
  doneCount: number;
  requiredCount: number;
  footer?: string;
}): ReactElement {
  const open = steps.filter((step) => !step.done);
  const done = steps.filter((step) => step.done);
  // The count is of required steps; it says so, and how many optional ones there are, so it never
  // disagrees with the rows below it (QA-031).
  const optional = steps.filter((step) => step.optional === true).length;
  const pct = requiredCount === 0 ? 100 : Math.round((doneCount / requiredCount) * 100);
  return (
    <div className={s.setup}>
      <div className={s.setupProgress}>
        <p className={s.setupCount}>
          {`${formatCount(doneCount)} of ${formatCount(requiredCount)} required done`}
          {optional > 0 && (
            <span className={s.meta}>{` · plus ${formatCount(optional)} optional`}</span>
          )}
        </p>
        <div
          className={s.setupBar}
          role="progressbar"
          aria-label={`${label}: ${formatCount(doneCount)} of ${formatCount(requiredCount)} required done`}
          aria-valuemin={0}
          aria-valuemax={requiredCount}
          aria-valuenow={doneCount}
        >
          <span className={s.setupFill} style={{ width: `${String(pct)}%` }} />
        </div>
      </div>
      {open.length > 0 && (
        <ol className={s.setupList} aria-label={`${label}: still to do`}>
          {open.map((step, i) => (
            <li key={step.id} className={s.setupItem}>
              <span
                className={cx(s.setupNumber, step.optional === true && s.setupNumberOptional)}
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <div className={s.setupText}>
                <h3 className={s.setupTitle}>
                  {step.title}
                  {step.optional === true ? (
                    <StatusPill tone="neutral" size="sm" label="Optional" />
                  ) : null}
                  {step.status != null && (
                    <StatusPill tone="waiting" size="sm" label={step.status} />
                  )}
                </h3>
                <p className={s.cardText}>{step.body}</p>
                {step.detail}
                {step.changed}
              </div>
              {(step.action || step.extra) && (
                <div className={s.setupActions}>
                  {step.extra}
                  {step.action && (
                    <ButtonLink
                      href={step.action.href}
                      variant={step.extra ? 'ghost' : 'navy'}
                      size="sm"
                    >
                      {step.action.label}
                    </ButtonLink>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {done.length > 0 && (
        <ul className={s.setupDone} aria-label={`${label}: done`}>
          {done.map((step) => (
            <li key={step.id} className={s.setupDoneItem}>
              <Icon name="check" size={18} />
              <div className={s.setupDoneText}>
                <p className={s.setupDoneLine}>
                  <span className={s.strong}>{step.title}</span>
                  {step.summary != null && <span className={s.meta}>{` · ${step.summary}`}</span>}
                </p>
                {/* FR3: a done item still says who last changed it, with its History. */}
                {step.changed}
              </div>
              {step.optional === true && <StatusPill tone="neutral" size="sm" label="Optional" />}
              <StatusPill tone="done" size="sm" label="Done" />
            </li>
          ))}
        </ul>
      )}
      {footer !== undefined && <p className={s.meta}>{footer}</p>}
    </div>
  );
}
